import localForage from 'localforage'
import sessionStorageDriver from 'localforage-sessionstoragewrapper'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'

import { useAsyncEffect } from './useAsyncEffect'

const STORAGES = {
	localStorage: localForage.createInstance({
		name: 'localStorage',
		driver: localForage.LOCALSTORAGE,
	}),
	sessionStorage: localForage.createInstance({
		name: 'sessionStorage',
		driver: sessionStorageDriver._driver,
	}),
	localForage,
} as const

const useBrowserStorage = <T>(
	key: string,
	initialState: T,
	storageName: keyof typeof STORAGES = 'localForage',
): [T, Dispatch<SetStateAction<T>>, boolean] => {
	const storage = STORAGES[storageName]
	const [state, setState] = useState<T>(initialState)
	const [isLoading, setLoading] = useState(true)
	useAsyncEffect(
		// oxlint-disable-next-line react-hooks/exhaustive-deps
		async status => {
			setLoading(true)
			try {
				const item = (await storage.getItem(key)) as T | null
				if (status.active && item) {
					setState(item)
				}
			} finally {
				if (status.active) {
					setLoading(false)
				}
			}
		},
		[key, storage],
	)

	useEffect(() => {
		if (state) {
			void storage.setItem(key, state)
		} else {
			void storage.removeItem(key)
		}
	}, [key, state, storage])

	return [state, setState, isLoading]
}

export { useBrowserStorage }
